// config.schema.json source — `~/.avatar/autopilot-config.json` (spec §18
// "Cấu hình và compatibility manifest"). Defaults below are copied verbatim
// from the spec's example JSON block; `.min()/.max()` on `limits.*` are D2
// (plan.md): "thêm object `limits` với default + min/max validation" —
// defaults there MUST match `runtime-limits-from-effective-config.ts` (P2)
// exactly, both files hardcode the same spec-pinned numbers independently
// (test_config_schema_limits_defaults_match_runtime_limits_module_and_
// enforce_min_max asserts the two agree; there is no shared constant module
// because config-schema.ts must not import runtime code, and vice versa —
// each is its own source pinned to the same spec section).
//
// `permissions_allow_fallback` (default false, N18): sole producer of the
// `permissions.allow` fallback flag consumed by mission-settings-profile.ts
// (P3). `worker_model` (default "claude-sonnet-5", D03/N24): P2's argv
// builder reads model from effective-config; omitting this key from the
// schema would let zod strip it silently, so the runner would never receive
// a model override.
import { z } from "zod";
import {
  BUDGET_CEILING_MUST_BE_POSITIVE,
  DEFAULT_MAX_ATTEMPTS_PER_MISSION,
} from "./config-budget-ceilings.js";
import { DEPLOY_TARGETS } from "./init-schema.js";
import { MattermostConfigSchema } from "./mattermost-config-schema.js";
import { QuietHoursSchema } from "./quiet-hours-schema.js";
import { ReleaseRecipeSchema, ReleaseRecipeTimeoutSecSchema } from "./release-recipe-schema.js";

// Bug G (P14): release-flow-after-done wiring (REVIEWING -> Draft MR ->
// REVIEW_REMOTE -> WAITING_GATE_G3 -> DEPLOY -> VERIFY -> DONE) reads
// remote/target from HERE instead of literals — absent (undefined) means
// the mission has no release target configured, so `run` leaves the
// mission at "done" once tickets finish (today's behavior, unchanged) —
// same "undefined => skip this subsystem" idiom mattermostTransport already
// uses (supervisor-mission-run.ts's own doc comment).
const ReleaseConfigSchema = z
  .object({
    remote: z.string().min(1),
    target_branch: z.string().min(1),
    // Recipe shape reused verbatim from verification-schema.ts — deploy is a
    // local side-effect recipe (v1: §14 "chỉ recipe cục bộ, KHÔNG deploy
    // thật"), verify's smoke recipe is the SAME contract a ticket's own
    // verification_recipe already uses. Both optional (H14, 2026-09-29):
    // absent deploy_recipe ⇒ G3 is still requested (it records the release
    // decision; the MR is merged by hand) but approving it deploys NOTHING —
    // `done_without_deploy`; deploy_recipe
    // without verify_recipe is refused before G3 (no unverifiable deploy).
    // Both feed the release manifest hash G3 is bound to.
    // A3: same shape, but `cwd` must stay inside the mission worktree.
    deploy_recipe: ReleaseRecipeSchema.optional(),
    verify_recipe: ReleaseRecipeSchema.optional(),
    // A4: run cap per recipe, seconds (absent ⇒ 60); both feed the G3 hash.
    deploy_timeout_sec: ReleaseRecipeTimeoutSecSchema.optional(),
    verify_timeout_sec: ReleaseRecipeTimeoutSecSchema.optional(),
    /**
     * P18: origin of the GitLab REST API, when it is not simply
     * `https://<host of the git remote>`. Two cases need it:
     *   - a self-hosted GitLab whose API lives on a different origin than the
     *     host the git remote points at;
     *   - a local fake GitLab on `http://127.0.0.1:<port>`, which is the only
     *     way to exercise the Draft-MR -> REVIEW_REMOTE -> G3 leg end to end
     *     without a real server.
     *
     * Absent => the client keeps its existing `https://<host>` fallback, so
     * every mission configured before this field behaves identically.
     *
     * This is NOT where the transport rule lives: the value still passes
     * through `isSecureOrLoopbackBaseUrl`, which refuses plaintext http at a
     * non-loopback authority BEFORE the PAT is ever attached to a request.
     * Declaring a bad origin here cannot downgrade `PRIVATE-TOKEN` to
     * cleartext — it just gets refused.
     */
    gitlab_base_url: z.string().min(1).optional(),
    // 06k F-05: môi trường recipe deploy THẬT tới — G3 ủy nhiệm đòi = deploy_target
    // của mission; staging/production còn cần opt-in. Không vào hash G3.
    deploy_environment: z.enum(DEPLOY_TARGETS).optional(),
    allow_delegated_g3_above_qa: z.boolean().optional(),
  })
  .strict();

const LimitsSchema = z
  .object({
    prompt_max_kib: z.number().int().min(64).max(4096).default(512),
    line_max_mib: z.number().int().min(1).max(16).default(1),
    raw_log_max_mib: z.number().int().min(8).max(1024).default(64),
    stderr_ring_kib: z.number().int().min(16).max(1024).default(64),
  })
  .strict()
  .default({
    prompt_max_kib: 512,
    line_max_mib: 1,
    raw_log_max_mib: 64,
    stderr_ring_kib: 64,
  });

const LogsConfigSchema = z
  .object({
    retention_days: z.number().int().positive().default(14),
    max_total_mb: z.number().int().positive().default(1024),
  })
  .strict()
  .default({ retention_days: 14, max_total_mb: 1024 });

export const AutopilotConfigSchema = z
  .object({
    schema_version: z.literal(1).default(1),
    mattermost: MattermostConfigSchema,
    poll_interval_sec: z.number().int().positive().default(60),
    poll_interval_gate_sec: z.number().int().positive().default(15),
    network_timeout_sec: z.number().int().positive().default(10),
    local_control_tick_ms: z.number().int().positive().default(1000),
    heartbeat_sec: z.number().int().positive().default(10),
    lease_stale_sec: z.number().int().positive().default(60),
    startup_timeout_sec: z.number().int().positive().default(120),
    shutdown_grace_sec: z.number().int().positive().default(20),
    abort_challenge_ttl_sec: z.number().int().positive().default(300),
    timeouts_min: z.record(z.string(), z.number().int().positive()).default({
      survey: 45,
      plan: 30,
      testcase: 30,
      implement: 60,
      test: 30,
      fix: 45,
      review: 45,
      decision: 20,
      revert: 30,
      deploy: 30,
    }),
    max_counted_failures: z.number().int().positive().default(3),
    split_after_timeouts: z.number().int().positive().default(2),
    max_fix_cycles_without_progress: z.number().int().positive().default(3),
    infrastructure_retry_limit: z.number().int().positive().default(3),
    external_outage_escalate_min: z.number().int().positive().default(30),
    rate_limit_signatures: z.array(z.string()).default([]),
    backoff_min: z.array(z.number().int().positive()).default([15, 30, 60]),
    // M1 (code-reviewer-260909-p7-m3.md): in-tick retry backoff base for
    // outbox-delivery-worker.ts's proven-pre-send retry (429 only) — a
    // DIFFERENT ladder from `backoff_min` (which is the cross-tick
    // quota-sleep ladder, in minutes). This one is short (ms), scoped to a
    // single control-loop tick's dense retry window.
    outbox_retry_backoff_ms: z.number().int().positive().default(200),
    // P19c: how many times ONE tick may re-attempt a single outbox op.
    // supervisor-transport-tick.ts requires this as an injected value — it
    // was the one MattermostTransportDeps field with no config source, so
    // any caller had to invent a literal (the §18 smell this project keeps
    // paying for).
    outbox_max_attempts_per_tick: z.number().int().positive().default(3),
    no_progress_reminder_hours: z.number().int().positive().default(6),
    quiet_hours: QuietHoursSchema.nullable().default(null),
    timezone: z.string().min(1).default("Asia/Ho_Chi_Minh"),
    max_sessions_per_hour: z.number().int().nonnegative().default(0),
    max_internal_subagents: z.number().int().positive().default(3),
    g2_ticket_threshold: z.number().int().positive().default(15),
    // G2 (P16, spec §8): "Mission nhỏ CHỈ waived nếu G0 đã ủy nhiệm". G0 is
    // where the human approves "default được ủy nhiệm và chính sách G2", so
    // the delegation lives in operator-owned config — NEVER in worker output,
    // which would let the governed party grant itself the waiver (the Bug K
    // shape). Default false = fail-closed: without an explicit delegation a
    // small mission still requests G2 from a human.
    g2_waive_delegated_by_g0: z.boolean().default(false),
    logs: LogsConfigSchema,
    outbox_max_pending: z.number().int().positive().default(10_000),
    min_free_disk_mb: z.number().int().positive().default(2048),
    limits: LimitsSchema,
    // N18: producer duy nhất của cờ fallback permissions.allow (P3 Req 8).
    permissions_allow_fallback: z.boolean().default(false),
    // D03/N24: worker luôn nhận --model từ effective-config, không phụ
    // thuộc settings model của user (probe 08/09: model mặc định của user
    // lỗi trên binary 2.1.153, claude-sonnet-5 hoạt động).
    worker_model: z.string().min(1).default("claude-sonnet-5"),
    // P13 Agent B + 06g R5 (audit M2, D1): trần chi phí/attempt cho MỘT mission,
    // có thì BẮT BUỘC > 0 (`0 = không giới hạn` ⇒ config_invalid). 3.38.1
    // (Luke 2026-10-14): trần USD TÙY CHỌN, KHÔNG default — vắng ⇒ không trần
    // USD; trần attempt giữ mặc định 60.
    max_cost_usd_per_mission: z
      .number()
      .positive({ error: BUDGET_CEILING_MUST_BE_POSITIVE })
      .optional(),
    max_attempts_per_mission: z
      .number()
      .int()
      .positive({ error: BUDGET_CEILING_MUST_BE_POSITIVE })
      .default(DEFAULT_MAX_ATTEMPTS_PER_MISSION),
    release: ReleaseConfigSchema.optional(),
  })
  .strict();

export type AutopilotConfig = z.infer<typeof AutopilotConfigSchema>;
